import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchPublicSiteIdentity, resolvePublicSiteRefByAgentId } from "../../services/publicSitesApi";
import {
  clearPersistedPublicSiteIdentity,
  normalizePublicAgentId,
  isNumericPublicSiteRef,
  normalizePublicSiteToken,
  persistPublicSiteIdentityRef,
  resolveAgentHotelPricingFromSiteIdentity,
  resolvePublicSiteBootstrapCandidate,
  resolvePublicSiteId,
  resolvePublicSiteDescription,
  resolvePublicSiteKeywords,
  resolvePublicSiteTitle,
  writeRuntimePublicSiteIdentity,
} from "./publicSiteIdentity";
import { useAgentEntitlements } from "../agentEntitlements/useAgentEntitlements";
import { useAgentCommissionSettings } from "../agentEntitlements/useAgentCommissionSettings";
import { useAppContext } from "../../contexts/AppContext";
import { appendPublicSiteToken } from "../../utils/publicSiteUrl";
import PublicSiteAccessibilityShell from "./accessibility/PublicSiteAccessibilityShell";
import { isTripMasterSurface } from "../../surfaces/tripMaster/tripMasterEnv";

export const PublicSiteContext = createContext(null);
const PUBLIC_SITE_BOOT_PERF = "PUBLIC_SITE_BOOT_PERF";
const PUBLIC_SITE_CONTEXT_DEDUP_TTL_MS = 5000;
const PUBLIC_SITE_CONTEXT_CACHE_TTL_MS = 5 * 60 * 1000;
export const PUBLIC_SITE_PRICING_SETTINGS_STORAGE_KEY =
  "hr_public_site_pricing_settings";
const publicSiteCallerLogOnceByToken = new Set();
const sharedPublicSiteIdentityCache = new Map();
const PUBLIC_SITE_IDENTITY_REQUEST_SUFFIX = "|/api/public-sites/by-token/:token";

export function resetPublicSiteIdentityCacheForTests() {
  sharedPublicSiteIdentityCache.clear();
  sharedPublicSiteIdentityInFlight.clear();
  sharedPublicSiteIdentityControllers.clear();
  sharedPublicSiteIdentityBlockedRetry.clear();
}

function peekSharedCachedPublicSiteIdentity(siteRef) {
  const normalized = normalizePublicSiteToken(siteRef);
  if (!normalized) return null;
  const requestKey = `${normalized}${PUBLIC_SITE_IDENTITY_REQUEST_SUFFIX}`;
  const cached = sharedPublicSiteIdentityCache.get(requestKey);
  if (cached && cached.expiresAt > Date.now() && cached.value) {
    return { token: normalized, identity: cached.value };
  }
  return null;
}
const sharedPublicSiteIdentityInFlight = new Map();
const sharedPublicSiteIdentityControllers = new Map();
const sharedPublicSiteIdentityBlockedRetry = new Map();
const sharedPublicApiGuardOnceByKeyAction = new Set();

function logBootPerf(event, data = {}) {
  if (import.meta.env.DEV) {
    console.info(PUBLIC_SITE_BOOT_PERF, event, data);
  }
}

function emitPublicSiteCallerLogOnce({ token, pathname, search, source, required, invalidSiteRef }) {
  if (!import.meta.env.DEV) return;
  if (typeof window === "undefined" || window.__ALLOW_LEGACY_INGEST !== true) return;
  const key = String(token || invalidSiteRef || "__missing__");
  if (publicSiteCallerLogOnceByToken.has(key)) return;
  publicSiteCallerLogOnceByToken.add(key);
  }

function emitPublicSiteContextLifecycleTrace(message, data = {}) {
  if (!import.meta.env.DEV) return;
  if (typeof window === "undefined" || window.__ALLOW_LEGACY_INGEST !== true) return;
  }

function emitPublicApiGuardLogOnce({ key, action, details = {} }) {
  if (!import.meta.env.DEV) return;
  if (typeof window === "undefined" || window.__ALLOW_LEGACY_INGEST !== true) return;
  const guardKey = `${String(key || "")}|${String(action || "")}`;
  if (!key || sharedPublicApiGuardOnceByKeyAction.has(guardKey)) return;
  sharedPublicApiGuardOnceByKeyAction.add(guardKey);
  }

function appendPublicSiteDebugLog(event, data = {}) {
  try {
    if (typeof window === "undefined") return;
    const prev = JSON.parse(
      window.sessionStorage.getItem("__PUBLIC_SITE_IDENTITY_LOGS__") || "[]"
    );
    const entry = {
      event,
      data,
      timestamp: Date.now(),
      pathname: window.location.pathname,
      search: window.location.search,
    };
    const next = Array.isArray(prev) ? prev.concat(entry) : [entry];
    window.sessionStorage.setItem(
      "__PUBLIC_SITE_IDENTITY_LOGS__",
      JSON.stringify(next.slice(-500))
    );
    // eslint-disable-next-line no-console
    console.info("[PUBLIC_SITE_IDENTITY_TRACE]", entry);
  } catch (_) {
    /* ignore */
  }
}

function resolveIdentitySlug(siteIdentity, fallbackToken = null) {
  return (
    String(
      siteIdentity?.slug ||
        siteIdentity?.public_site_slug ||
        siteIdentity?.site_slug ||
        fallbackToken ||
        ""
    ).trim() || null
  );
}

function publishPublicSiteIdentityForSeo(siteIdentity, options = {}) {
  if (typeof window === "undefined") return;
  const fallbackSiteRef = normalizePublicSiteToken(options?.fallbackSiteRef);
  const fallbackSlug = String(options?.fallbackSlug || "").trim() || null;
  const fallbackAgentId = normalizePublicAgentId(options?.fallbackAgentId);
  const source = String(options?.source || "").trim() || null;
  const agentId = siteIdentity?.agent_id ?? fallbackAgentId ?? null;
  const siteSettings = siteIdentity?.site_settings || null;
  const siteTitle = resolvePublicSiteTitle(siteIdentity);
  const siteDescription = resolvePublicSiteDescription(siteIdentity);
  const siteKeywords = resolvePublicSiteKeywords(siteIdentity);
  const resolvedSiteId = normalizePublicSiteToken(resolvePublicSiteId(siteIdentity), fallbackSiteRef);
  const resolvedSlug = resolveIdentitySlug(siteIdentity, fallbackSlug || resolvedSiteId);
  window.__HR_PUBLIC_SITE_AGENT_ID = agentId;
  window.__HR_PUBLIC_SITE_SITE_SETTINGS = siteSettings;
  window.__HR_PUBLIC_SITE_TITLE = siteTitle;
  window.__HR_PUBLIC_SITE_DESCRIPTION = siteDescription;
  window.__HR_PUBLIC_SITE_KEYWORDS = siteKeywords;
  writeRuntimePublicSiteIdentity(
    resolvedSiteId
      ? {
          resolvedSiteId,
          resolvedAgentId: agentId,
          resolvedSlug,
          source: source || "public_site_provider",
        }
      : null
  );
  window.dispatchEvent(
    new CustomEvent("hr-public-site-identity", {
      detail: { agentId, siteSettings, siteTitle, siteDescription, siteKeywords },
    })
  );
}

function persistPublicSitePricingSettings(siteIdentity) {
  if (typeof window === "undefined") return;
  const settings =
    siteIdentity?.payment_settings?.public_pricing_settings ||
    siteIdentity?.public_pricing_settings ||
    null;
  if (!settings || typeof settings !== "object") return;
  try {
    const siteId = resolvePublicSiteId(siteIdentity);
    window.__HR_PUBLIC_SITE_PRICING_SETTINGS = settings;
    window.sessionStorage.setItem(
      PUBLIC_SITE_PRICING_SETTINGS_STORAGE_KEY,
      JSON.stringify({ siteId, settings, persistedAt: Date.now() })
    );
  } catch (_) {
    /* ignore storage failures */
  }
}

function clearPublicSitePricingSettings() {
  if (typeof window === "undefined") return;
  try {
    delete window.__HR_PUBLIC_SITE_PRICING_SETTINGS;
    window.sessionStorage.removeItem(PUBLIC_SITE_PRICING_SETTINGS_STORAGE_KEY);
  } catch (_) {
    /* ignore storage failures */
  }
}

export function PublicSiteProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const bootstrapCandidate = useMemo(
    () =>
      resolvePublicSiteBootstrapCandidate({
        search: location.search,
        pathname: location.pathname,
        hostname: typeof window !== "undefined" ? window.location.hostname : "",
      }),
    [location.pathname, location.search]
  );
  const cachedBoot = peekSharedCachedPublicSiteIdentity(bootstrapCandidate.siteRef);
  const [siteIdentity, setSiteIdentity] = useState(() => cachedBoot?.identity ?? null);
  const [siteIdentityStatus, setSiteIdentityStatus] = useState(() =>
    cachedBoot?.identity ? "ready" : "idle"
  );
  const [siteIdentityError, setSiteIdentityError] = useState(null);
  const [resolvedSiteId, setResolvedSiteId] = useState(() =>
    cachedBoot?.identity ? resolvePublicSiteId(cachedBoot.identity) : null
  );
  const [siteSource, setSiteSource] = useState(null);
  const currentSiteIdRef = useRef(cachedBoot?.token || "");
  const siteIdentityRef = useRef(null);
  const inFlightRef = useRef(sharedPublicSiteIdentityInFlight);
  const cacheRef = useRef(sharedPublicSiteIdentityCache);
  const lastRequestAtRef = useRef(new Map());
  const controllersRef = useRef(sharedPublicSiteIdentityControllers);
  const blockedRetryRef = useRef(sharedPublicSiteIdentityBlockedRetry);
  const resolutionTraceDedupRef = useRef("");

  useEffect(() => {
    siteIdentityRef.current = siteIdentity;
  }, [siteIdentity]);

  const loadSiteIdentity = useCallback(async (siteId) => {
    const raw = String(siteId || "").trim();
    const normalized = normalizePublicSiteToken(raw);
    const requestKey = `${normalized}${PUBLIC_SITE_IDENTITY_REQUEST_SUFFIX}`;
    emitPublicSiteContextLifecycleTrace("[PUBLIC_SITE_CONTEXT_TRACE]", {
      action: "load_called",
      rawSiteRef: raw || null,
      normalizedSiteRef: normalized || null,
      requestKey,
      currentSiteRef: currentSiteIdRef.current || null,
      pathname: typeof window !== "undefined" ? window.location.pathname : null,
      search: typeof window !== "undefined" ? window.location.search : null,
    });
    logBootPerf("loadSiteIdentity called", { siteToken: normalized || null, rawSiteRef: raw || null });
    if (raw && isNumericPublicSiteRef(raw)) {
      currentSiteIdRef.current = "";
      const error = {
        code: "NUMERIC_SITE_ID_NOT_ALLOWED",
        message: "Public site URL must use ?siteId=<PUBLIC_TOKEN>, not a numeric id.",
      };
      setResolvedSiteId(null);
      setSiteIdentity(null);
      setSiteIdentityError(error);
      setSiteIdentityStatus("error");
      setSiteSource(null);
      publishPublicSiteIdentityForSeo(null);
      clearPersistedPublicSiteIdentity();
      clearPublicSitePricingSettings();
      logBootPerf("error", { ...error, rawSiteRef: raw });
      return null;
    }
    if (!normalized) {
      currentSiteIdRef.current = "";
      logBootPerf("error", { code: "MISSING_SITE_TOKEN" });
      setResolvedSiteId(null);
      setSiteIdentity(null);
      setSiteIdentityError({ code: "MISSING_SITE_TOKEN", message: "siteId public token is required" });
      setSiteIdentityStatus("missing");
      setSiteSource(null);
      publishPublicSiteIdentityForSeo(null);
      clearPersistedPublicSiteIdentity();
      clearPublicSitePricingSettings();
      logBootPerf("loading cleared", { status: "missing" });
      if (import.meta.env.DEV) {
        console.warn("[PublicSite] missing siteId candidate", {
          pathname: typeof window !== "undefined" ? window.location.pathname : null,
          search: typeof window !== "undefined" ? window.location.search : null,
          host: typeof window !== "undefined" ? window.location.hostname : null,
        });
      }
      appendPublicSiteDebugLog("missing_site_token", {
        rawSiteRef: raw || null,
        normalizedSiteRef: normalized || null,
      });
      return null;
    }
    if (currentSiteIdRef.current === normalized) {
      logBootPerf("loadSiteIdentity skipped", { siteToken: normalized, reason: "already-loaded-or-loading" });
      return null;
    }
    const now = Date.now();
    const blockedRetry = blockedRetryRef.current.get(requestKey);
    if (blockedRetry) {
      emitPublicApiGuardLogOnce({
        key: requestKey,
        action: "blocked_retry",
        details: { siteToken: normalized, status: blockedRetry.status ?? null },
      });
      appendPublicSiteDebugLog("blocked_retry", {
        requestKey,
        siteToken: normalized,
        blockedStatus: blockedRetry.status ?? null,
      });
      return null;
    }
    const cachedEntry = cacheRef.current.get(requestKey);
    if (cachedEntry && cachedEntry.expiresAt > now && cachedEntry.value) {
      const cachedIdentity = cachedEntry.value;
      currentSiteIdRef.current = normalized;
      setResolvedSiteId(resolvePublicSiteId(cachedIdentity));
      setSiteIdentityStatus("ready");
      setSiteIdentityError(null);
      setSiteIdentity(cachedIdentity);
      publishPublicSiteIdentityForSeo(cachedIdentity);
      persistPublicSitePricingSettings(cachedIdentity);
      emitPublicApiGuardLogOnce({
        key: requestKey,
        action: "cache",
        details: { siteToken: normalized, source: "context-cache" },
      });
      logBootPerf("loadSiteIdentity skipped", {
        siteToken: normalized,
        reason: "context-cache-hit",
        ttlMs: cachedEntry.expiresAt - now,
      });
      return cachedIdentity;
    }
    const existingInFlight = inFlightRef.current.get(requestKey);
    if (existingInFlight) {
      emitPublicApiGuardLogOnce({
        key: requestKey,
        action: "inflight",
        details: { siteToken: normalized, source: "context-inflight" },
      });
      logBootPerf("loadSiteIdentity skipped", { siteToken: normalized, reason: "context-in-flight" });
      return existingInFlight;
    }
    const lastRequestAt = lastRequestAtRef.current.get(requestKey) || 0;
    if (now - lastRequestAt < PUBLIC_SITE_CONTEXT_DEDUP_TTL_MS && siteIdentityRef.current) {
      logBootPerf("loadSiteIdentity skipped", {
        siteToken: normalized,
        reason: "context-throttle",
        throttleMs: PUBLIC_SITE_CONTEXT_DEDUP_TTL_MS,
      });
      return siteIdentityRef.current;
    }
    lastRequestAtRef.current.set(requestKey, now);
    currentSiteIdRef.current = normalized;
    setResolvedSiteId(normalized);
    setSiteIdentityStatus("loading");
    setSiteIdentityError(null);
    for (const [key, controller] of controllersRef.current.entries()) {
      if (key === requestKey) continue;
      controller.abort();
      controllersRef.current.delete(key);
      inFlightRef.current.delete(key);
    }
    const controller = new AbortController();
    controllersRef.current.set(requestKey, controller);
    emitPublicApiGuardLogOnce({
      key: requestKey,
      action: "network",
      details: { siteToken: normalized, source: "context-network" },
    });
    logBootPerf("fetch metadata start", { siteRef: normalized });
    const requestPromise = (async () => {
      try {
      if (import.meta.env.DEV) {
        console.info("[PublicSite] loading site identity", { siteRef: normalized });
      }
      const res = await fetchPublicSiteIdentity(normalized, { signal: controller.signal });
      if (res?.status === 499 || res?.data?.error?.code === "REQUEST_ABORTED") {
        blockedRetryRef.current.set(requestKey, { status: 499, blockedAt: Date.now() });
        logBootPerf("loadSiteIdentity skipped", { siteToken: normalized, reason: "request-aborted" });
        return null;
      }
      if (res?.status === 204) {
        logBootPerf("fetch metadata no-content", { siteRef: normalized, status: 204 });
        setSiteIdentityStatus((prev) => (prev === "empty" ? prev : "empty"));
        setSiteIdentity((prev) => (prev == null ? prev : null));
        setSiteIdentityError((prev) => (prev == null ? prev : null));
        publishPublicSiteIdentityForSeo(null);
        clearPersistedPublicSiteIdentity();
        clearPublicSitePricingSettings();
        return null;
      }
      const body = res?.data || {};
      const nextSiteId = resolvePublicSiteId(body);
      if (res?.status >= 200 && res?.status < 300 && body?.success && nextSiteId) {
        logBootPerf("fetch metadata success", {
          siteRef: normalized,
          siteToken: nextSiteId,
          status: res?.status,
        });
        setSiteIdentity(body);
        setResolvedSiteId(nextSiteId);
        setSiteIdentityStatus("ready");
        persistPublicSiteIdentityRef({
          siteRef: normalized,
          siteId: nextSiteId,
          siteIdentity: body,
        });
        cacheRef.current.set(requestKey, {
          value: body,
          expiresAt: Date.now() + PUBLIC_SITE_CONTEXT_CACHE_TTL_MS,
        });
        blockedRetryRef.current.delete(requestKey);
        publishPublicSiteIdentityForSeo(body);
        persistPublicSitePricingSettings(body);
        logBootPerf("loading cleared", { status: "ready", siteToken: nextSiteId });
        if (import.meta.env.DEV) {
          console.info("[PublicSite] site identity ready", {
            siteRef: normalized,
            siteId: nextSiteId,
            agentId: body?.agent_id ?? null,
          });
        }
        appendPublicSiteDebugLog("identity_ready", {
          requestKey,
          siteToken: normalized,
          resolvedSiteId: nextSiteId,
          agentId: body?.agent_id ?? null,
        });
        return body;
      }
      logBootPerf(res?.status === 404 ? "fetch metadata 404" : "error", {
        siteRef: normalized,
        status: res?.status,
        error: body?.error || null,
      });
      currentSiteIdRef.current = "";
      setResolvedSiteId(null);
      setSiteIdentity(null);
      setSiteIdentityStatus("error");
      publishPublicSiteIdentityForSeo(null);
      clearPersistedPublicSiteIdentity();
      clearPublicSitePricingSettings();
      setSiteIdentityError(
        body?.error || {
          code: res?.status === 404 ? "SITE_NOT_FOUND" : "SITE_IDENTITY_LOAD_FAILED",
          message:
            res?.status === 404
              ? "לא נמצא אתר ציבורי עבור המזהה שהתקבל."
              : "טעינת פרטי האתר הציבורי נכשלה.",
        }
      );
      blockedRetryRef.current.set(requestKey, { status: res?.status ?? 520, blockedAt: Date.now() });
      appendPublicSiteDebugLog("identity_http_error", {
        requestKey,
        siteToken: normalized,
        httpStatus: res?.status ?? null,
        errorCode: body?.error?.code ?? null,
        errorMessage: body?.error?.message ?? null,
      });
      logBootPerf("loading cleared", { status: "error", httpStatus: res?.status });
      return null;
      } catch (err) {
      const isTimeout =
        err?.code === "ECONNABORTED" || String(err?.message || "").toLowerCase().includes("timeout");
      logBootPerf(isTimeout ? "metadata timeout" : "error", {
        siteRef: normalized,
        code: err?.code,
        message: err?.message,
      });
      currentSiteIdRef.current = "";
      setResolvedSiteId(null);
      setSiteIdentity(null);
      setSiteIdentityStatus("error");
      publishPublicSiteIdentityForSeo(null);
      clearPersistedPublicSiteIdentity();
      clearPublicSitePricingSettings();
      setSiteIdentityError({
        code: isTimeout ? "SITE_IDENTITY_TIMEOUT" : "SITE_IDENTITY_NETWORK_ERROR",
        message: isTimeout
          ? "טעינת פרטי האתר הציבורי ארכה זמן רב מדי. נסו שוב בעוד רגע."
          : err?.message || "שגיאת תקשורת בטעינת פרטי האתר הציבורי.",
      });
      blockedRetryRef.current.set(requestKey, {
        status: isTimeout ? 408 : 520,
        blockedAt: Date.now(),
      });
      appendPublicSiteDebugLog("identity_network_error", {
        requestKey,
        siteToken: normalized,
        code: err?.code ?? null,
        message: err?.message ?? null,
        status: isTimeout ? 408 : 520,
      });
      logBootPerf("loading cleared", { status: "error", reason: isTimeout ? "timeout" : "network" });
      return null;
      }
    })().finally(() => {
      inFlightRef.current.delete(requestKey);
      controllersRef.current.delete(requestKey);
    });
    inFlightRef.current.set(requestKey, requestPromise);
    return requestPromise;
  }, []);

  const bootstrapSiteRef = bootstrapCandidate.siteRef || "";
  const bootstrapPathAgentId = normalizePublicAgentId(bootstrapCandidate.pathAgentId);
  const bootstrapRequiredKey = bootstrapCandidate.required ? "required" : "optional";
  const bootstrapSource = bootstrapCandidate.source || null;

  useEffect(() => {
    setSiteSource(bootstrapSource);
  }, [bootstrapSource]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const tracedResolvedSiteId = normalizePublicSiteToken(
      resolvedSiteId || bootstrapSiteRef || bootstrapCandidate.invalidSiteRef || null
    );
    const resolvedAgentId =
      siteIdentity?.agent_id ??
      siteIdentity?.agentId ??
      bootstrapCandidate?.resolvedAgentId ??
      bootstrapCandidate?.persistedIdentity?.resolvedAgentId ??
      bootstrapPathAgentId ??
      null;
    const resolvedSlug = resolveIdentitySlug(
      siteIdentity,
      bootstrapCandidate?.resolvedSlug || tracedResolvedSiteId
    );
    const trace = {
      pathname: location.pathname || "",
      search: location.search || "",
      host: typeof window !== "undefined" ? window.location?.host || "" : "",
      resolvedAgentId,
      resolvedSlug,
      resolvedSiteId: tracedResolvedSiteId,
      source: siteSource || bootstrapSource || null,
      hasCachedIdentity: Boolean(bootstrapCandidate?.hasCachedIdentity),
    };
    const dedupKey = JSON.stringify(trace);
    if (resolutionTraceDedupRef.current === dedupKey) return;
    resolutionTraceDedupRef.current = dedupKey;
    // eslint-disable-next-line no-console
    console.info("[PUBLIC_SITE_IDENTITY_SSOT_TRACE]", trace);
  }, [
    bootstrapCandidate.hasCachedIdentity,
    bootstrapCandidate.invalidSiteRef,
    bootstrapCandidate.persistedIdentity,
    bootstrapCandidate.resolvedAgentId,
    bootstrapCandidate.resolvedSlug,
    bootstrapPathAgentId,
    bootstrapSiteRef,
    bootstrapSource,
    location.pathname,
    location.search,
    resolvedSiteId,
    siteIdentity,
    siteSource,
  ]);

  useEffect(() => {
    appendPublicSiteDebugLog("provider_state", {
      bootstrapSiteRef,
      bootstrapSource,
      required: bootstrapRequiredKey === "required",
      resolvedSiteId,
      siteIdentityStatus,
      siteIdentityErrorCode: siteIdentityError?.code ?? null,
      siteIdentityErrorMessage: siteIdentityError?.message ?? null,
    });
  }, [
    bootstrapRequiredKey,
    bootstrapSiteRef,
    bootstrapSource,
    resolvedSiteId,
    siteIdentityError,
    siteIdentityStatus,
  ]);

  useEffect(() => {
    let cancelled = false;
    emitPublicSiteCallerLogOnce({
      token: bootstrapSiteRef || null,
      pathname: location.pathname,
      search: location.search,
      source: bootstrapSource,
      required: bootstrapRequiredKey === "required",
      invalidSiteRef: bootstrapCandidate.invalidSiteRef || null,
    });
    logBootPerf("start", {
      siteRef: bootstrapSiteRef || null,
      invalidSiteRef: bootstrapCandidate.invalidSiteRef || null,
      source: bootstrapSource,
      required: bootstrapRequiredKey === "required",
      pathname: location.pathname,
      search: location.search,
    });
    const runBootstrap = async () => {
      if (bootstrapCandidate.invalidSiteRef) {
        await loadSiteIdentity(bootstrapCandidate.invalidSiteRef);
        return;
      }
      if (bootstrapSiteRef) {
        await loadSiteIdentity(bootstrapSiteRef);
        return;
      }
      if (bootstrapPathAgentId) {
        setSiteIdentityStatus("loading");
        setSiteIdentityError(null);
        const byAgent = await resolvePublicSiteRefByAgentId(bootstrapPathAgentId);
        if (cancelled) return;
        if (byAgent?.success && byAgent?.siteRef) {
          publishPublicSiteIdentityForSeo(null, {
            fallbackSiteRef: byAgent.siteRef,
            fallbackAgentId: byAgent.resolvedAgentId,
            fallbackSlug: byAgent.resolvedSlug,
            source: "path:agentId",
          });
          await loadSiteIdentity(byAgent.siteRef);
          return;
        }
      }
      if (bootstrapRequiredKey === "required") {
        await loadSiteIdentity(null);
      }
    };
    runBootstrap();
    return () => {
      cancelled = true;
    };
  }, [
    bootstrapCandidate.invalidSiteRef,
    bootstrapPathAgentId,
    bootstrapRequiredKey,
    bootstrapSiteRef,
    loadSiteIdentity,
    location.pathname,
    bootstrapSource,
  ]);

  useEffect(() => {
    if (isTripMasterSurface()) return;
    if (bootstrapSource !== "localStorage") return;
    if (!bootstrapSiteRef) return;
    const params = new URLSearchParams(location.search || "");
    if (params.get("siteId")) return;
    const target = appendPublicSiteToken(
      { pathname: location.pathname, search: location.search },
      bootstrapSiteRef
    );
    logBootPerf("restored token into url", {
      siteToken: bootstrapSiteRef,
      pathname: location.pathname,
      previousSearch: location.search,
      nextSearch: target.search,
    });
    navigate(target, { replace: true });
  }, [bootstrapSiteRef, bootstrapSource, location.pathname, location.search, navigate]);

  useEffect(() => {
    return () => {
      emitPublicSiteContextLifecycleTrace("[PUBLIC_SITE_CONTEXT_TRACE]", {
        action: "provider_unmount_cleanup",
        pathname: typeof window !== "undefined" ? window.location.pathname : null,
        search: typeof window !== "undefined" ? window.location.search : null,
        currentSiteRef: currentSiteIdRef.current || null,
      });
      // Do not abort global in-flight identity requests on transient unmount.
      // The API layer dedups by request key and can be shared by remounted consumers.
      controllersRef.current.clear();
      lastRequestAtRef.current.clear();
    };
  }, []);

  const agentHotelPricingFromSite = useMemo(
    () => resolveAgentHotelPricingFromSiteIdentity(siteIdentity),
    [siteIdentity]
  );
  const resolvedPublicSlug = useMemo(
    () => resolveIdentitySlug(siteIdentity, bootstrapCandidate?.resolvedSlug || resolvedSiteId),
    [bootstrapCandidate?.resolvedSlug, resolvedSiteId, siteIdentity]
  );
  const resolvedPublicAgentId =
    siteIdentity?.agent_id ??
    siteIdentity?.agentId ??
    bootstrapCandidate?.resolvedAgentId ??
    bootstrapCandidate?.persistedIdentity?.resolvedAgentId ??
    bootstrapPathAgentId ??
    null;

  const value = useMemo(
    () => ({
      siteId: resolvedSiteId,
      agentId: resolvedPublicAgentId,
      resolvedAgentId: resolvedPublicAgentId,
      resolvedSlug: resolvedPublicSlug,
      branding: siteIdentity?.branding || null,
      resolvedSiteId,
      siteIdentity,
      siteIdentityStatus,
      siteIdentityError,
      siteSource,
      requiresPublicSite: bootstrapCandidate.required,
      siteSettings: siteIdentity?.site_settings || null,
      publicSitePricingSettings:
        siteIdentity?.payment_settings?.public_pricing_settings ||
        siteIdentity?.public_pricing_settings ||
        null,
      agentHotelPricingFromSite,
      loadSiteIdentity,
    }),
    [
      agentHotelPricingFromSite,
      bootstrapCandidate?.persistedIdentity?.resolvedAgentId,
      bootstrapCandidate.required,
      bootstrapPathAgentId,
      resolvedPublicAgentId,
      resolvedPublicSlug,
      resolvedSiteId,
      siteIdentity,
      siteIdentityStatus,
      siteIdentityError,
      siteSource,
      siteIdentity?.site_settings,
      loadSiteIdentity,
    ]
  );

  if (import.meta.env.DEV) {
    }

  return (
    <PublicSiteContext.Provider value={value}>
      <PublicSiteAccessibilityShell>{children}</PublicSiteAccessibilityShell>
    </PublicSiteContext.Provider>
  );
}

export function usePublicSite() {
  const ctx = useContext(PublicSiteContext);
  if (!ctx) {
    throw new Error("usePublicSite must be used within PublicSiteProvider");
  }
  return ctx;
}

/** Context מהסייט הציבורי כשקיים Provider; ללא עלות כשאין (למשל דשבורד סוכן / Loader רחב). */
export function useOptionalPublicSite() {
  return useContext(PublicSiteContext);
}

/**
 * מחירון HR לתצוגה ציבורית: מזהות האתר (סוכן הבעלים) כש־requiresPublicSite,
 * אחרת entitlements של הסשן (אפליקציית סוכן).
 */
export function usePublicAgentHotelPricing() {
  const ctx = useContext(PublicSiteContext);
  const { hotelHrCommissionPercentage } = useAgentEntitlements();
  const { fixedHotelCommission } = useAgentCommissionSettings();
  const { getHrCommissionPercentHotels } = useAppContext();
  return useMemo(() => {
    if (ctx?.requiresPublicSite) {
      if (ctx.siteIdentityStatus !== "ready") return null;
      return ctx.agentHotelPricingFromSite;
    }
    const hr = Number(hotelHrCommissionPercentage);
    if (Number.isFinite(hr)) {
      return {
        hotelHrCommissionPercentage: hr,
        fixedHotelCommission: Number(fixedHotelCommission) || 0,
      };
    }
    const globalHr = Number(getHrCommissionPercentHotels());
    if (Number.isFinite(globalHr)) {
      return { hotelHrCommissionPercentage: globalHr, fixedHotelCommission: 0 };
    }
    return null;
  }, [
    ctx?.requiresPublicSite,
    ctx?.siteIdentityStatus,
    ctx?.agentHotelPricingFromSite,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
    getHrCommissionPercentHotels,
  ]);
}
